import { ReactElement, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { TooltipHover } from "components/designV2/tooltip/tooltip";
import { CustomIcon } from "components/icon/customIcon.component";
import { ColorV2 } from "config/colors";
import { UnitTeamDropdown } from "web-apps/company/components/common/filters/unitTeamDropdown.component";
import { SettingsV2PageTitle } from "../companySettingsV2.styled";
import {
  OrganizationCreateDialog,
  OrganizationEditPanel,
  OrganizationMembersPanel,
} from "./organizationSettings.dialogs.component";
import type {
  OrganizationEditOperation,
  OrganizationEditTarget,
} from "./organizationSettings.dialogs.component";
import {
  filterOrganizationUnits,
  flattenOrganizationUnits,
  getUnitPersonCount,
  getUnitTeamCount,
} from "./organizationSettings.logic";
import type { OrganizationUnit } from "./organizationSettings.logic";
import {
  AddTeamRow,
  EmptyState,
  EmptyText,
  EmptyTitle,
  ErrorBox,
  ErrorRow,
  Header,
  HeaderActions,
  HeaderCopy,
  IconButton,
  Intro,
  LoadingText,
  Meta,
  Page,
  RowActions,
  TeamCount,
  TeamList,
  TeamName,
  TeamRow,
  UnitCard,
  UnitHeader,
  UnitList,
  UnitTitle,
  UnitTitleRow,
  UnitTitleWrap,
} from "./organizationSettings.styled";
import { useOrganizationSettings } from "./useOrganizationSettings.hook";

type CreateTarget =
  | { readonly kind: "unit" }
  | {
      readonly kind: "team";
      readonly unitId: number;
      readonly unitName: string;
    }
  | null;

export const OrganizationSettings = (): ReactElement => {
  const { t } = useTranslation();
  const organization = useOrganizationSettings();
  const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>([]);
  const [expandedUnits, setExpandedUnits] = useState<number[]>([]);
  const [createTarget, setCreateTarget] = useState<CreateTarget>(null);
  const [editTarget, setEditTarget] = useState<OrganizationEditTarget | null>(
    null,
  );
  const [membersTeamId, setMembersTeamId] = useState<number | null>(null);
  const hasOrganizationFilter = selectedTeamIds.length > 0;

  const units = useMemo(
    () => flattenOrganizationUnits(organization.structure),
    [organization.structure],
  );
  const unitTeamOptions = useMemo(
    () =>
      units.map((unit) => ({
        unitId: unit.id,
        unitName: unit.name,
        teams: unit.workGroups.map((team) => ({
          id: team.id,
          name: team.name,
        })),
      })),
    [units],
  );

  useEffect(() => {
    setExpandedUnits((current) => {
      const next = units.map((unit) => unit.id);
      return current.length === 0
        ? next
        : Array.from(new Set([...current, ...next]));
    });
  }, [units]);

  useEffect(() => {
    const availableTeamIds = new Set(
      units.flatMap((unit) => unit.workGroups.map((team) => String(team.id))),
    );
    setSelectedTeamIds((current) =>
      current.filter((teamId) => availableTeamIds.has(teamId)),
    );
  }, [units]);

  const visibleUnits = useMemo(
    () => filterOrganizationUnits({ selectedTeamIds, units }),
    [selectedTeamIds, units],
  );

  const toggleUnit = (unitId: number): void => {
    setExpandedUnits((current) =>
      current.includes(unitId)
        ? current.filter((id) => id !== unitId)
        : [...current, unitId],
    );
  };

  const renderUnit = (unit: OrganizationUnit): ReactElement => {
    const isExpanded = expandedUnits.includes(unit.id);
    const teamCount = unit.workGroups.length;
    const personCount = getUnitPersonCount(unit);

    return (
      <UnitCard key={unit.id}>
        <UnitHeader>
          <UnitTitleWrap>
            <UnitTitleRow>
              <UnitTitle>{unit.name}</UnitTitle>
              <TooltipHover text={t("OrganizationSettings.UnitSettings")}>
                <IconButton
                  type="button"
                  aria-label={t("OrganizationSettings.EditUnit", {
                    name: unit.name,
                  })}
                  onClick={() =>
                    setEditTarget({
                      kind: "unit",
                      id: unit.id,
                      entityId: unit.entityId,
                      name: unit.name,
                      teamCount: getUnitTeamCount({
                        entityId: unit.entityId,
                        unitId: unit.id,
                        units,
                      }),
                    })
                  }
                >
                  <CustomIcon name="v2/pen" size="18px" color={ColorV2.Blue1} />
                </IconButton>
              </TooltipHover>
            </UnitTitleRow>
            <Meta>
              {t("OrganizationSettings.UnitTeamCount", {
                count: teamCount,
              })}
              {", "}
              {t("OrganizationSettings.UnitPersonCount", {
                count: personCount,
              })}
            </Meta>
          </UnitTitleWrap>
          <IconButton
            type="button"
            aria-expanded={isExpanded}
            aria-label={
              isExpanded
                ? t("OrganizationSettings.CollapseUnit")
                : t("OrganizationSettings.ExpandUnit")
            }
            onClick={() => toggleUnit(unit.id)}
          >
            <CustomIcon
              name={isExpanded ? "v2/up-chevron" : "v2/down-chevron"}
              size="20px"
              color={ColorV2.Blue1}
            />
          </IconButton>
        </UnitHeader>
        {isExpanded && (
          <>
            {teamCount > 0 ? (
              <TeamList>
                {unit.workGroups.map((team) => (
                  <TeamRow key={team.id}>
                    <TeamName>{team.name}</TeamName>
                    <TeamCount>
                      {t("OrganizationSettings.MemberCount", {
                        count: team.workers?.length ?? 0,
                      })}
                    </TeamCount>
                    <RowActions>
                      <TooltipHover
                        text={t("OrganizationSettings.TeamSettings")}
                      >
                        <IconButton
                          type="button"
                          aria-label={t("OrganizationSettings.EditTeam", {
                            name: team.name,
                          })}
                          onClick={() =>
                            setEditTarget({
                              kind: "team",
                              id: team.id,
                              memberCount: team.workers?.length ?? 0,
                              name: team.name,
                            })
                          }
                        >
                          <CustomIcon
                            name="v2/pen"
                            size="20px"
                            color={ColorV2.Blue1}
                          />
                        </IconButton>
                      </TooltipHover>
                      <TooltipHover
                        text={t("OrganizationSettings.ManageMembers")}
                      >
                        <IconButton
                          type="button"
                          aria-label={t(
                            "OrganizationSettings.ManageTeamMembers",
                            { name: team.name },
                          )}
                          onClick={() => setMembersTeamId(team.id)}
                        >
                          <CustomIcon
                            name="v2/person"
                            size="20px"
                            color={ColorV2.Blue1}
                          />
                        </IconButton>
                      </TooltipHover>
                    </RowActions>
                  </TeamRow>
                ))}
                <AddTeamRow>
                  <SecondaryButton
                    size="small"
                    iconName="v2/plus"
                    label={t("OrganizationSettings.AddTeam")}
                    onClick={() =>
                      setCreateTarget({
                        kind: "team",
                        unitId: unit.id,
                        unitName: unit.name,
                      })
                    }
                  />
                </AddTeamRow>
              </TeamList>
            ) : (
              <EmptyState>
                <EmptyTitle>
                  {t("OrganizationSettings.UnitHasNoTeams")}
                </EmptyTitle>
                <EmptyText>
                  {t("OrganizationSettings.UnitHasNoTeamsInfo")}
                </EmptyText>
                <SecondaryButton
                  size="small"
                  iconName="v2/plus"
                  label={t("OrganizationSettings.CreateTeam")}
                  onClick={() =>
                    setCreateTarget({
                      kind: "team",
                      unitId: unit.id,
                      unitName: unit.name,
                    })
                  }
                />
              </EmptyState>
            )}
          </>
        )}
      </UnitCard>
    );
  };

  const saveEdit = ({ name, target }: OrganizationEditOperation) => {
    if (target.kind === "team") {
      return organization.updateTeam({ name, teamId: target.id });
    }
    return organization.updateUnit({
      entityId: target.entityId,
      name,
      unitId: target.id,
    });
  };

  const deleteEdit = (target: OrganizationEditTarget) => {
    if (target.kind === "team") return organization.deleteTeam(target.id);
    return organization.deleteUnit({
      entityId: target.entityId,
      unitId: target.id,
    });
  };

  return (
    <Page>
      <Header>
        <HeaderCopy>
          <SettingsV2PageTitle>
            {t("OrganizationSettings.Title")}
          </SettingsV2PageTitle>
          <Intro>{t("OrganizationSettings.Intro")}</Intro>
        </HeaderCopy>
        <HeaderActions>
          <UnitTeamDropdown
            label={t("OrganizationSettings.UnitTeam")}
            placeholder={t("OrganizationSettings.AllUnitsTeamsVisible")}
            emptyText={t("OrganizationSettings.NoUnitTeamMatches")}
            options={unitTeamOptions}
            selectedTeamIds={selectedTeamIds}
            onSelectedTeamIdsChange={setSelectedTeamIds}
          />
          <PrimaryButton
            size="large"
            iconName="v2/plus"
            label={t("OrganizationSettings.CreateUnit")}
            disabled={organization.isLoading || organization.error != null}
            onClick={() => setCreateTarget({ kind: "unit" })}
          />
        </HeaderActions>
      </Header>

      {organization.error && units.length > 0 && (
        <ErrorRow>
          <ErrorBox>{t("OrganizationSettings.LoadFailed")}</ErrorBox>
          <SecondaryButton
            size="small"
            label={t("OrganizationSettings.Retry")}
            onClick={() => void organization.reload()}
          />
        </ErrorRow>
      )}
      {organization.error && units.length === 0 ? (
        <EmptyState>
          <ErrorBox>{t("OrganizationSettings.LoadFailed")}</ErrorBox>
          <SecondaryButton
            size="small"
            label={t("OrganizationSettings.Retry")}
            onClick={() => void organization.reload()}
          />
        </EmptyState>
      ) : organization.isLoading && units.length === 0 ? (
        <LoadingText>{t("OrganizationSettings.Loading")}</LoadingText>
      ) : visibleUnits.length > 0 ? (
        <UnitList>{visibleUnits.map(renderUnit)}</UnitList>
      ) : (
        <EmptyState>
          <EmptyTitle>
            {hasOrganizationFilter
              ? t("OrganizationSettings.NoSearchResults")
              : t("OrganizationSettings.NoUnits")}
          </EmptyTitle>
          <EmptyText>
            {hasOrganizationFilter
              ? t("OrganizationSettings.NoSearchResultsInfo")
              : t("OrganizationSettings.NoUnitsInfo")}
          </EmptyText>
          {!hasOrganizationFilter && (
            <PrimaryButton
              size="small"
              iconName="v2/plus"
              label={t("OrganizationSettings.CreateUnit")}
              onClick={() => setCreateTarget({ kind: "unit" })}
            />
          )}
        </EmptyState>
      )}

      <OrganizationCreateDialog
        kind={createTarget?.kind ?? "unit"}
        parentName={
          createTarget?.kind === "team" ? createTarget.unitName : undefined
        }
        isOpen={createTarget != null}
        onClose={() => setCreateTarget(null)}
        onSave={(name) =>
          createTarget?.kind === "team"
            ? organization.createTeam({ name, unitId: createTarget.unitId })
            : organization.createUnit(name)
        }
      />
      <OrganizationEditPanel
        target={editTarget}
        onClose={() => setEditTarget(null)}
        onSave={saveEdit}
        onDelete={deleteEdit}
      />
      <OrganizationMembersPanel
        teamId={membersTeamId}
        onClose={() => setMembersTeamId(null)}
        loadMembers={organization.loadTeamMembers}
        loadWorkers={organization.loadCompanyWorkers}
        assignWorker={organization.assignWorker}
        removeWorker={organization.removeWorker}
      />
    </Page>
  );
};
